/**
 * Centralized query key factory for type-safe cache management
 * Hierarchical structure allows partial invalidation of related queries
 */

export const queryKeys = {
  recipes: {
    all: ['recipes'] as const,
    lists: () => [...queryKeys.recipes.all, 'list'] as const,
    list: (searchTerm?: string, category?: string, timeFilter?: string) =>
      [...queryKeys.recipes.lists(), { searchTerm, category, timeFilter }] as const,
    paginated: (page: number, pageSize: number, searchTerm?: string, category?: string, timeFilter?: string) =>
      [...queryKeys.recipes.lists(), { page, pageSize, searchTerm, category, timeFilter }] as const,
    detail: (id: string) => [...queryKeys.recipes.all, 'detail', id] as const,
  },

  ingredients: {
    all: ['ingredients'] as const,
    lists: () => [...queryKeys.ingredients.all, 'list'] as const,
    paginated: (page: number, pageSize: number, searchTerm?: string, category?: string) =>
      [...queryKeys.ingredients.lists(), { page, pageSize, searchTerm, category }] as const,
    detail: (id: string) => [...queryKeys.ingredients.all, 'detail', id] as const,
    categories: () => [...queryKeys.ingredients.all, 'categories'] as const,
  },

  inventory: {
    all: ['inventory'] as const,
    lists: () => [...queryKeys.inventory.all, 'list'] as const,
    paginated: (page: number, pageSize: number, searchTerm?: string, status?: string, category?: string) =>
      [...queryKeys.inventory.lists(), { page, pageSize, searchTerm, status, category }] as const,
    lowStock: (threshold?: number) => [...queryKeys.inventory.all, 'low-stock', threshold] as const,
    transactions: (ingredientId?: string) =>
      [...queryKeys.inventory.all, 'transactions', ingredientId] as const,
  },

  orders: {
    all: ['orders'] as const,
    lists: () => [...queryKeys.orders.all, 'list'] as const,
    paginated: (page: number, pageSize: number, status?: string) =>
      [...queryKeys.orders.lists(), { page, pageSize, status }] as const,
    detail: (id: string) => [...queryKeys.orders.all, 'detail', id] as const,
  },

  templates: {
    all: ['templates'] as const,
    lists: () => [...queryKeys.templates.all, 'list'] as const,
    detail: (id: string) => [...queryKeys.templates.all, 'detail', id] as const,
  },

  weeklyMenus: {
    all: ['weeklyMenus'] as const,
    lists: () => [...queryKeys.weeklyMenus.all, 'list'] as const,
    detail: (id: string) => [...queryKeys.weeklyMenus.all, 'detail', id] as const,
  },

  user: {
    all: ['user'] as const,
    profile: () => [...queryKeys.user.all, 'profile'] as const,
    roles: () => [...queryKeys.user.all, 'roles'] as const,
    permissions: (userId?: string) => [...queryKeys.user.all, 'permissions', userId] as const,
    onboarding: (userId?: string) => [...queryKeys.user.all, 'onboarding', userId] as const,
  },

  auditLogs: {
    all: ['auditLogs'] as const,
    paginated: (page: number, pageSize: number, filters?: any) =>
      [...queryKeys.auditLogs.all, { page, pageSize, filters }] as const,
  },

  notifications: {
    all: ['notifications'] as const,
    list: (read?: boolean, limit?: number) =>
      [...queryKeys.notifications.all, 'list', { read, limit }] as const,
    unreadCount: () => [...queryKeys.notifications.all, 'unread-count'] as const,
    preferences: () => [...queryKeys.notifications.all, 'preferences'] as const,
  },

  sysco: {
    all: ['sysco'] as const,
    confirmations: (page?: number, filters?: any) => 
      page ? [...queryKeys.sysco.all, 'confirmations', page, filters] as const : [...queryKeys.sysco.all, 'confirmations'] as const,
    confirmation: (id: string) => [...queryKeys.sysco.all, 'confirmation', id] as const,
    stats: () => [...queryKeys.sysco.all, 'stats'] as const,
    invoices: (page?: number, filters?: any) => 
      page ? [...queryKeys.sysco.all, 'invoices', page, filters] as const : [...queryKeys.sysco.all, 'invoices'] as const,
    invoice: (id: string) => [...queryKeys.sysco.all, 'invoice', id] as const,
    orderInvoices: (orderId: string) => [...queryKeys.sysco.all, 'order-invoices', orderId] as const,
    variances: (filters?: any) => [...queryKeys.sysco.all, 'variances', filters] as const,
    invoiceStats: () => [...queryKeys.sysco.all, 'invoice-stats'] as const,
    orderGuides: (page?: number, filters?: any) => 
      page ? [...queryKeys.sysco.all, 'order-guides', page, filters] as const : [...queryKeys.sysco.all, 'order-guides'] as const,
    orderGuide: (itemId: string) => [...queryKeys.sysco.all, 'order-guide', itemId] as const,
    orderGuideStats: () => [...queryKeys.sysco.all, 'order-guide-stats'] as const,
    orderGuideCategories: () => [...queryKeys.sysco.all, 'order-guide-categories'] as const,
    orderGuideBrands: () => [...queryKeys.sysco.all, 'order-guide-brands'] as const,
  },
} as const;
